import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentIcon, ReceiptIcon, CurrencyIcon, CheckIcon, XIcon,
  RefreshIcon, FilterIcon, EyeIcon, DownloadIcon, PlusIcon,
  CalendarIcon, UsersIcon, AlertIcon,
} from '../../components/Icons';

// DownloadIcon pode não existir — fallback
const DlIcon = (p) => (
  <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const token = () => localStorage.getItem('kialuga_token');
const auth  = () => ({ Authorization: `Bearer ${token()}` });
const fmt   = (v) => parseFloat(v || 0).toLocaleString('pt-AO', { minimumFractionDigits: 2 });
const fmtKz = (v) => {
  const n = parseFloat(v || 0);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M Kz';
  if (n >= 1_000)     return (n / 1_000).toFixed(0) + 'K Kz';
  return n.toLocaleString() + ' Kz';
};

const ESTADO_COR = {
  emitida:   { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
  paga:      { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
  cancelada: { bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
  rascunho:  { bg: '#F8FAFC', text: '#475569', border: '#CBD5E1' },
};

function BadgeEstado({ estado }) {
  const c = ESTADO_COR[estado] || ESTADO_COR.rascunho;
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '600',
    }}>
      {estado.charAt(0).toUpperCase() + estado.slice(1)}
    </span>
  );
}

// ── Modal: Detalhe da Factura ─────────────────────────────
function ModalFactura({ id, onClose, onEstadoChange }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [alterandoEstado, setAlterandoEstado] = useState(false);
  const [novoEstado, setNovoEstado] = useState('');
  const [refPagamento, setRefPagamento] = useState('');

  useEffect(() => {
    fetch(`${BASE}/admin/facturas/${id}`, { headers: auth() })
      .then(r => r.json())
      .then(d => { if (d.sucesso) setDados(d); })
      .finally(() => setCarregando(false));
  }, [id]);

  const mudarEstado = async () => {
    if (!novoEstado) return;
    const r = await fetch(`${BASE}/admin/facturas/${id}/estado`, {
      method: 'PATCH',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ estado: novoEstado, referencia_pagamento: refPagamento }),
    });
    const d = await r.json();
    if (d.sucesso) { onEstadoChange(); onClose(); }
  };

  const baixarPDF = () => {
    window.open(`${BASE}/admin/facturas/${id}/pdf?token=${token()}`, '_blank');
  };

  if (carregando) return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          A carregar...
        </div>
      </div>
    </div>
  );

  if (!dados) return null;
  const { factura: f, itens } = dados;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto' }}
           onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {f.numero_factura}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {f.fornecedor_nome} · {f.fornecedor_email}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <BadgeEstado estado={f.estado} />
            <button className="btn btn-outline btn-sm" onClick={baixarPDF}>
              <DlIcon size={14} /> PDF
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              <XIcon size={16} />
            </button>
          </div>
        </div>

        {/* Meta */}
        <div className="grid-4" style={{ marginBottom: '20px' }}>
          {[
            ['Emissão', new Date(f.data_emissao).toLocaleDateString('pt-PT')],
            ['Vencimento', new Date(f.data_vencimento).toLocaleDateString('pt-PT')],
            ['Período',
              `${new Date(f.periodo_inicio).toLocaleDateString('pt-PT')} — ${new Date(f.periodo_fim).toLocaleDateString('pt-PT')}`
            ],
            ['Itens', `${itens.length} reserva${itens.length !== 1 ? 's' : ''}`],
          ].map(([l, v]) => (
            <div key={l} style={{ background: 'var(--surface)', borderRadius: '8px', padding: '10px 12px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>{l}</div>
              <div style={{ fontSize: '12px', fontWeight: '600' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Tabela itens */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '16px' }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              {['Descrição', 'Datas', 'Aluguer', 'Base s/IVA', `IVA (${f.iva_pct}%)`, 'Comissão'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Descrição' || h === 'Datas' ? 'left' : 'right', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itens.map((item, i) => (
              <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--surface)' }}>
                <td style={{ padding: '8px 10px', maxWidth: '200px' }}>
                  <div style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.descricao}
                  </div>
                </td>
                <td style={{ padding: '8px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {new Date(item.data_inicio).toLocaleDateString('pt-PT')} → {new Date(item.data_fim).toLocaleDateString('pt-PT')}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(item.valor_aluguer)} Kz</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(item.valor_base)} Kz</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{fmt(item.iva_valor)} Kz</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: 'var(--orange)' }}>{fmt(item.valor_comissao)} Kz</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totais */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '14px 20px', minWidth: '240px' }}>
            {[
              ['Subtotal (s/ IVA)', fmt(f.subtotal) + ' Kz', false],
              [`IVA (${f.iva_pct}%)`, fmt(f.iva_valor) + ' Kz', false],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', color: 'var(--text-secondary)' }}>
                <span>{l}</span><span>{v}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: '700' }}>
              <span>Total</span>
              <span style={{ color: 'var(--orange)' }}>{fmt(f.total)} Kz</span>
            </div>
          </div>
        </div>

        {/* Alterar estado */}
        {f.estado !== 'cancelada' && f.estado !== 'paga' && (
          <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '14px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>ALTERAR ESTADO</label>
              <select value={novoEstado} onChange={e => setNovoEstado(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg)', fontSize: '13px' }}>
                <option value="">Seleccionar...</option>
                <option value="paga">Marcar como Paga</option>
                <option value="cancelada">Cancelar</option>
              </select>
            </div>
            {novoEstado === 'paga' && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>REF. PAGAMENTO (opcional)</label>
                <input value={refPagamento} onChange={e => setRefPagamento(e.target.value)}
                  placeholder="Ex: TRF-2026-001"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg)', fontSize: '13px' }} />
              </div>
            )}
            <button className="btn btn-primary btn-sm" onClick={mudarEstado} disabled={!novoEstado}>
              <CheckIcon size={14} /> Confirmar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal: Gerar Nova Factura ─────────────────────────────
function ModalGerar({ onClose, onGerada }) {
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedorId, setFornecedorId] = useState('');
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    fetch(`${BASE}/admin/facturas/fatur%C3%A1veis`, { headers: auth() })
      .then(r => r.json())
      .then(d => { if (d.sucesso) setFornecedores(d.fornecedores); });

    // Pré-preencher com o mês anterior
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimoDia   = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    setPeriodoInicio(primeiroDia.toISOString().split('T')[0]);
    setPeriodoFim(ultimoDia.toISOString().split('T')[0]);
  }, []);

  const gerar = async () => {
    setErro('');
    setCarregando(true);
    try {
      const r = await fetch(`${BASE}/admin/facturas/gerar`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fornecedor_id: fornecedorId, periodo_inicio: periodoInicio, periodo_fim: periodoFim }),
      });
      const d = await r.json();
      if (d.sucesso) { onGerada(d); onClose(); }
      else setErro(d.mensagem);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>Gerar Nova Factura</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><XIcon size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={labelStyle}>FORNECEDOR</label>
            <select value={fornecedorId} onChange={e => setFornecedorId(e.target.value)} style={inputStyle}>
              <option value="">Seleccionar fornecedor...</option>
              {fornecedores.map(f => (
                <option key={f.id} value={f.id}>
                  {f.nome} — {parseInt(f.reservas_por_facturar)} reservas ({fmtKz(f.comissao_total)} comissão)
                </option>
              ))}
            </select>
            {!fornecedores.length && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Sem fornecedores com reservas por facturar.
              </div>
            )}
          </div>

          <div className="grid-2">
            <div>
              <label style={labelStyle}>PERÍODO — INÍCIO</label>
              <input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>PERÍODO — FIM</label>
              <input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ background: 'var(--info-bg)', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', fontSize: '11px', color: 'var(--info)' }}>
            IVA de 14% incluído no valor da comissão (método: 14/114 × comissão). A factura inclui todas as reservas concluídas do período ainda não facturadas.
          </div>

          {erro && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: 'var(--danger)' }}>
              {erro}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={gerar}
              disabled={!fornecedorId || !periodoInicio || !periodoFim || carregando}>
              {carregando ? 'A gerar...' : <><DocumentIcon size={14} /> Gerar Factura</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Página Principal ──────────────────────────────────────
export default function AdminFacturas() {
  const [facturas, setFacturas]       = useState([]);
  const [stats, setStats]             = useState(null);
  const [carregando, setCarregando]   = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroAno, setFiltroAno]     = useState(new Date().getFullYear());
  const [filtroMes, setFiltroMes]     = useState('');
  const [detalheId, setDetalheId]     = useState(null);
  const [mostrarGerar, setMostrarGerar] = useState(false);
  const [sucesso, setSucesso]         = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    const params = new URLSearchParams();
    if (filtroEstado) params.set('estado', filtroEstado);
    if (filtroAno)    params.set('ano', filtroAno);
    if (filtroMes)    params.set('mes', filtroMes);

    const [fRes, sRes] = await Promise.all([
      fetch(`${BASE}/admin/facturas?${params}`, { headers: auth() }).then(r => r.json()),
      fetch(`${BASE}/admin/facturas/stats`, { headers: auth() }).then(r => r.json()),
    ]);
    if (fRes.sucesso) setFacturas(fRes.facturas);
    if (sRes.sucesso) setStats(sRes.stats);
    setCarregando(false);
  }, [filtroEstado, filtroAno, filtroMes]);

  useEffect(() => { carregar(); }, [carregar]);

  const onGerada = (d) => {
    setSucesso(`✅ ${d.mensagem}`);
    carregar();
    setTimeout(() => setSucesso(''), 5000);
  };

  const anos = [2025, 2026, 2027];
  const meses = [
    ['', 'Todos'], ['1','Jan'], ['2','Fev'], ['3','Mar'], ['4','Abr'],
    ['5','Mai'], ['6','Jun'], ['7','Jul'], ['8','Ago'], ['9','Set'],
    ['10','Out'], ['11','Nov'], ['12','Dez'],
  ];

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div>
          <div className="topbar-title">Módulo Financeiro — Facturas</div>
          <div className="topbar-sub">Facturas electrónicas de comissões (10% sobre alugueres/vendas)</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-ghost btn-sm" onClick={carregar}><RefreshIcon size={14} /></button>
          <button className="btn btn-primary" onClick={() => setMostrarGerar(true)}>
            <PlusIcon size={14} /> Gerar Factura
          </button>
        </div>
      </div>

      <div className="page-content">
        {sucesso && (
          <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: 'var(--success)', display: 'flex', gap: '8px' }}>
            <CheckIcon size={16} />{sucesso}
          </div>
        )}

        {/* KPIs */}
        {stats && (
          <div className="stats-grid stats-grid-4" style={{ marginBottom: '20px' }}>
            <div className="stat-card orange">
              <div className="stat-label">Total Facturado</div>
              <div className="stat-value">{fmtKz(stats.valor_total)}</div>
              <div className="stat-change">{parseInt(stats.total)} factura{parseInt(stats.total) !== 1 ? 's' : ''}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #16A34A' }}>
              <div className="stat-label">Valor Pago</div>
              <div className="stat-value" style={{ color: '#16A34A' }}>{fmtKz(stats.valor_pago)}</div>
              <div className="stat-change">{parseInt(stats.pagas)} pagas</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--orange)' }}>
              <div className="stat-label">Valor Pendente</div>
              <div className="stat-value" style={{ color: 'var(--orange)' }}>{fmtKz(stats.valor_pendente)}</div>
              <div className="stat-change">{parseInt(stats.emitidas)} emitidas</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">IVA Total</div>
              <div className="stat-value">{fmtKz(stats.iva_total)}</div>
              <div className="stat-change">14% incluído</div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="card" style={{ marginBottom: '16px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterIcon size={14} color="var(--text-muted)" />
            <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px' }}>
              <option value="">Todos os estados</option>
              <option value="emitida">Emitidas</option>
              <option value="paga">Pagas</option>
              <option value="cancelada">Canceladas</option>
              <option value="rascunho">Rascunho</option>
            </select>
            <select value={filtroAno} onChange={e => setFiltroAno(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px' }}>
              {anos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px' }}>
              {meses.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {(filtroEstado || filtroMes) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setFiltroEstado(''); setFiltroMes(''); }}>
                <XIcon size={12} /> Limpar
              </button>
            )}
          </div>
        </div>

        {/* Tabela */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {carregando ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <RefreshIcon size={20} /> A carregar...
            </div>
          ) : facturas.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <DocumentIcon size={40} color="var(--text-muted)" style={{ marginBottom: '12px', opacity: 0.4 }} />
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>Sem facturas encontradas</div>
              <button className="btn btn-primary btn-sm" onClick={() => setMostrarGerar(true)}>
                <PlusIcon size={14} /> Gerar primeira factura
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                    {['Nº Factura', 'Fornecedor', 'Emissão', 'Período', 'Itens', 'Total', 'Estado', 'Acções'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {facturas.map(f => (
                    <tr key={f.id} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--orange)', fontFamily: 'monospace' }}>{f.numero_factura}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: '500' }}>{f.fornecedor_nome}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.fornecedor_email}</div>
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {new Date(f.data_emissao).toLocaleDateString('pt-PT')}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(f.periodo_inicio).toLocaleDateString('pt-PT')} →<br />
                        {new Date(f.periodo_fim).toLocaleDateString('pt-PT')}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <span style={{ background: 'var(--surface)', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: '600' }}>
                          {f.total_itens}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {fmt(f.total)} Kz
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '400' }}>
                          IVA: {fmt(f.iva_valor)} Kz
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <BadgeEstado estado={f.estado} />
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-ghost btn-sm" title="Ver detalhe"
                            onClick={() => setDetalheId(f.id)}>
                            <EyeIcon size={14} />
                          </button>
                          <button className="btn btn-ghost btn-sm" title="Download PDF"
                            onClick={() => window.open(`${BASE}/admin/facturas/${f.id}/pdf?token=${token()}`, '_blank')}>
                            <DlIcon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Nota */}
        <div style={{ marginTop: '14px', padding: '10px 14px', background: 'var(--surface)', borderRadius: '8px', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <AlertIcon size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span>
            As facturas referem-se à comissão KiAluga de <strong>10%</strong> sobre o valor de aluguer/venda.
            O IVA de <strong>14%</strong> está incluído na comissão (método: 14/114 × comissão).
            Só se geram facturas para reservas com estado <em>concluida</em> ainda não facturadas.
          </span>
        </div>
      </div>

      {/* Modais */}
      {detalheId && (
        <ModalFactura
          id={detalheId}
          onClose={() => setDetalheId(null)}
          onEstadoChange={carregar}
        />
      )}
      {mostrarGerar && (
        <ModalGerar
          onClose={() => setMostrarGerar(false)}
          onGerada={onGerada}
        />
      )}
    </>
  );
}

// ── Estilos partilhados ───────────────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: '20px',
};
const modalStyle = {
  background: 'var(--card-bg)', borderRadius: '14px',
  padding: '24px', width: '100%', maxWidth: '600px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const labelStyle = {
  fontSize: '10px', color: 'var(--text-muted)',
  fontWeight: '600', display: 'block', marginBottom: '5px',
};
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid var(--border)', background: 'var(--bg)',
  fontSize: '13px', color: 'var(--text-primary)', boxSizing: 'border-box',
};
